// Maps the personal setup (status/contexts/goal) to a concrete practice plan.
// Pure client-side — zero AI cost. Used by the setup plan screen and the
// "For you" strip on Practice, so both always agree.
//
// Rec shape: { key, type: "scenario"|"journey"|"own", slug?, journeyId?, prefillKey? }

const GOAL_RECS = {
  "make-official": [
    { type: "scenario", slug: "define-the-relationship" },
    { type: "journey", journeyId: "dating-to-relationship" },
  ],
  "meet-someone": [
    { type: "scenario", slug: "texting-someone-you-like" },
    { type: "scenario", slug: "first-date" },
  ],
  confidence: [
    { type: "scenario", slug: "first-date" },
    { type: "scenario", slug: "setting-boundaries" },
  ],
  "express-needs": [
    { type: "scenario", slug: "setting-boundaries" },
    { type: "journey", journeyId: "holding-your-ground" },
  ],
  "deeper-communication": [
    { type: "scenario", slug: "mental-load-talk" },
    { type: "scenario", slug: "conflict-repair" },
  ],
  "resolve-conflict": [
    { type: "scenario", slug: "conflict-repair" },
    { type: "scenario", slug: "mental-load-talk" },
  ],
  "share-load": [
    { type: "scenario", slug: "mental-load-talk" },
    { type: "scenario", slug: "conflict-repair" },
  ],
  "move-on": [
    { type: "journey", journeyId: "moving-on" },
    { type: "scenario", slug: "handling-rejection" },
  ],
  closure: [
    { type: "journey", journeyId: "moving-on" },
    { type: "scenario", slug: "breakup-conversation" },
  ],
  "raise-promotion": [
    { type: "own", prefillKey: "raise-promotion" },
    { type: "scenario", slug: "setting-boundaries" },
  ],
  "speak-up": [
    { type: "own", prefillKey: "speak-up" },
    { type: "scenario", slug: "responding-to-gaslighting" },
  ],
};

const CONTEXT_EXTRAS = {
  family: { type: "scenario", slug: "setting-boundaries" },
  "dating-apps": { type: "scenario", slug: "persistent-match" },
  "home-conflict": { type: "scenario", slug: "conflict-repair" },
};

export function buildPlan(personal) {
  if (!personal) return [];
  const recs = [...(GOAL_RECS[personal.goal] || [])];
  for (const ctx of personal.contexts || []) {
    const extra = CONTEXT_EXTRAS[ctx];
    if (extra) recs.push(extra);
  }
  // Dedupe + cap at 3
  const seen = new Set();
  const out = [];
  for (const r of recs) {
    const key = r.slug || r.journeyId || r.prefillKey;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...r, key });
    if (out.length >= 3) break;
  }
  return out;
}

// A plan step counts as mastered only with a decent score; low scores mean "rehearse again".
const RETRY_THRESHOLD = 6;

export function sessionScore(session) {
  const f = session?.feedback;
  if (!f) return null;
  const parts = [f.clarityScore, f.empathyScore, f.boundaryScore].filter((n) => typeof n === "number");
  if (!parts.length) return null;
  return Math.round(parts.reduce((a, b) => a + b, 0) / parts.length);
}

// Annotates plan recs with progress: "done" | "retry" | "pending".
// Scenario steps are score-aware (weak score → rehearse again). A journey step is
// done when all its scenario slugs are completed (pass journeySteps: {id: [slugs]},
// e.g. from t("journeys.list", { returnObjects: true })). An "own" step is done
// after any completed bring-your-own session.
export function planWithProgress(plan, sessions, { journeySteps, since } = {}) {
  // Only sessions completed AFTER the plan was created count — the plan is a
  // fresh start, old history must not pre-tick its steps.
  const sinceTime = since ? new Date(since).getTime() : null;
  const bestBySlug = {};
  (sessions || []).forEach((s) => {
    if (s.status !== "COMPLETED" || !s.scenario?.slug) return;
    if (sinceTime && !Number.isNaN(sinceTime)) {
      const ended = new Date(s.endedAt || s.startedAt || 0).getTime();
      if (!ended || ended < sinceTime) return;
    }
    const score = sessionScore(s);
    const prev = bestBySlug[s.scenario.slug];
    if (prev === undefined || (score !== null && (prev === null || score > prev))) {
      bestBySlug[s.scenario.slug] = score;
    }
  });
  return plan.map((rec) => {
    if (rec.type === "own") {
      return { ...rec, state: "bring-your-own" in bestBySlug ? "done" : "pending" };
    }
    if (rec.type === "journey") {
      const steps = journeySteps?.[rec.journeyId]?.steps || journeySteps?.[rec.journeyId];
      const done = Array.isArray(steps) && steps.length > 0 && steps.every((slug) => slug in bestBySlug);
      return { ...rec, state: done ? "done" : "pending" };
    }
    if (!(rec.slug in bestBySlug)) return { ...rec, state: "pending" };
    const best = bestBySlug[rec.slug];
    const state = best !== null && best < RETRY_THRESHOLD ? "retry" : "done";
    return { ...rec, state, bestScore: best };
  });
}

// When every step of the current plan is done, the coach moves the user to the
// next goal in the chain — progression keeps going instead of a finished card.
export const NEXT_GOAL = {
  "meet-someone": "confidence",
  confidence: "express-needs",
  "make-official": "deeper-communication",
  "express-needs": "resolve-conflict",
  "deeper-communication": "resolve-conflict",
  "resolve-conflict": "share-load",
  "share-load": "deeper-communication",
  "move-on": "confidence",
  closure: "move-on",
  "raise-promotion": "speak-up",
  "speak-up": "express-needs",
};

// AsyncStorage key holding an unanswered status suggestion, so it survives the
// user closing the feedback screen and can be re-asked once (e.g. on Home).
export const PENDING_STATUS_KEY = "pendingStatusSuggestion";

// Applies a confirmed life-status change to the personal answers:
// - goal moves to the new status's default unless it still fits (work goals are
//   status-agnostic and survive)
// - freeText is dropped: it described the old situation and gets echoed in the
//   UI, so keeping it would put stale words in the coach's mouth
// - the "define" context only makes sense while dating
// - savedAt resets so plan progress starts fresh
export function applyStatusToPersonal(personal, newStatus) {
  const p = personal || {};
  const validGoals = GOALS_BY_STATUS[newStatus] || [];
  const keepGoal = validGoals.includes(p.goal) || WORK_GOALS.includes(p.goal);
  const contexts = (p.contexts || []).filter((c) => !(c === "define" && newStatus !== "dating"));
  return {
    ...p,
    status: newStatus,
    goal: keepGoal ? p.goal : validGoals[0],
    contexts,
    freeText: undefined,
    savedAt: new Date().toISOString(),
  };
}

// Personal answers older than this suggest a life update — nudge a check-in.
export const CHECKIN_AFTER_DAYS = 21;

export function needsCheckin(personal) {
  if (!personal?.savedAt) return false;
  const saved = new Date(personal.savedAt).getTime();
  if (Number.isNaN(saved)) return false;
  return Date.now() - saved > CHECKIN_AFTER_DAYS * 24 * 60 * 60 * 1000;
}

// Goal options offered per status; work goals appended when the work context is picked.
export const GOALS_BY_STATUS = {
  single: ["meet-someone", "confidence"],
  dating: ["make-official", "express-needs"],
  relationship: ["deeper-communication", "resolve-conflict"],
  married: ["resolve-conflict", "share-load"],
  breakup: ["move-on", "closure"],
};
export const WORK_GOALS = ["raise-promotion", "speak-up"];
